import { useEffect, useState } from 'react'

/**
 * Som de alerta do Tempo Real ("ding-dong") que funciona mesmo onde a voz (text-to-speech) não
 * existe, como em muitas TVs: o som é gerado pelo navegador (Web Audio), sem arquivo para baixar.
 * Navegador sem Web Audio usa um WAV gerado aqui mesmo.
 *
 * Navegadores só liberam som depois que alguém toca na página (regra de autoplay). Por isso o
 * primeiro toque, clique ou tecla em qualquer lugar libera o som, e as telas podem mostrar um
 * aviso enquanto ele estiver bloqueado (useAlertSoundLocked).
 */

const NOTES = [
  { frequency: 880, start: 0, duration: 0.55 },
  { frequency: 659.25, start: 0.45, duration: 0.9 },
]
const MIN_INTERVAL_MS = 3000

/** Nota base e uma oitava acima, mais fraca: som de campainha, mais fácil de ouvir na fábrica. */
const HARMONICS = [1, 2]

let context = null
let fallbackUrl = null
let unlocked = false
let lastPlay = 0
const listeners = new Set()

const notify = () => listeners.forEach((listener) => listener(!unlocked))

const getContext = () => {
  if (context) return context
  const AudioContextClass =
    typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext)
  if (!AudioContextClass) return null
  try {
    context = new AudioContextClass()
    context.onstatechange = () => {
      unlocked = context.state === 'running'
      notify()
    }
  } catch (e) {
    context = null
  }
  return context
}

/** WAV com as mesmas duas notas, para navegadores sem Web Audio. */
const buildFallbackUrl = () => {
  if (fallbackUrl) return fallbackUrl
  const rate = 22050
  const total = Math.ceil(rate * 1.4)
  const samples = new Int16Array(total)
  NOTES.forEach(({ frequency, start, duration }) => {
    const first = Math.floor(start * rate)
    const length = Math.floor(duration * rate)
    for (let i = 0; i < length && first + i < total; i++) {
      const t = i / rate
      const envelope = Math.min(1, t / 0.01) * Math.exp((-4 * t) / duration)
      const value =
        (Math.sin(2 * Math.PI * frequency * t) + 0.3 * Math.sin(4 * Math.PI * frequency * t)) *
        envelope *
        0.45
      samples[first + i] = Math.max(-1, Math.min(1, samples[first + i] / 32767 + value)) * 32767
    }
  })
  const buffer = new ArrayBuffer(44 + samples.length * 2)
  const view = new DataView(buffer)
  const write = (offset, text) =>
    text.split('').forEach((char, i) => view.setUint8(offset + i, char.charCodeAt(0)))
  write(0, 'RIFF')
  view.setUint32(4, 36 + samples.length * 2, true)
  write(8, 'WAVE')
  write(12, 'fmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true)
  view.setUint16(22, 1, true)
  view.setUint32(24, rate, true)
  view.setUint32(28, rate * 2, true)
  view.setUint16(32, 2, true)
  view.setUint16(34, 16, true)
  write(36, 'data')
  view.setUint32(40, samples.length * 2, true)
  samples.forEach((sample, i) => view.setInt16(44 + i * 2, sample, true))
  fallbackUrl = URL.createObjectURL(new Blob([buffer], { type: 'audio/wav' }))
  return fallbackUrl
}

/** Libera o som (precisa vir de um toque, clique ou tecla). */
export const unlockAlertSound = () => {
  const ctx = getContext()
  if (ctx) {
    try {
      // um som vazio dentro do gesto libera o áudio em Safari/WebKit e navegadores de TV
      const buffer = ctx.createBuffer(1, 1, 22050)
      const source = ctx.createBufferSource()
      source.buffer = buffer
      source.connect(ctx.destination)
      source.start(0)
      if (ctx.state !== 'running') ctx.resume().catch(() => {})
    } catch (e) {
      // segue para o resume
    }
    unlocked = ctx.state === 'running'
  } else {
    unlocked = true
  }
  notify()
}

/** Toca o "ding-dong" do alerta. Chamadas seguidas (menos de 3 s) tocam uma vez só. */
export const playAlertChime = () => {
  const now = Date.now()
  if (now - lastPlay < MIN_INTERVAL_MS) return
  lastPlay = now

  const ctx = getContext()
  if (ctx && ctx.state === 'running') {
    const at = ctx.currentTime + 0.02
    const output = ctx.createGain()
    output.gain.value = 0.6
    output.connect(ctx.destination)
    NOTES.forEach(({ frequency, start, duration }) => {
      HARMONICS.forEach((harmonic) => {
        const oscillator = ctx.createOscillator()
        const gain = ctx.createGain()
        oscillator.type = 'sine'
        oscillator.frequency.value = frequency * harmonic
        const peak = harmonic === 1 ? 0.8 : 0.22
        gain.gain.setValueAtTime(0.0001, at + start)
        gain.gain.exponentialRampToValueAtTime(peak, at + start + 0.015)
        gain.gain.exponentialRampToValueAtTime(0.0001, at + start + duration)
        oscillator.connect(gain)
        gain.connect(output)
        oscillator.start(at + start)
        oscillator.stop(at + start + duration + 0.05)
      })
    })
    return
  }

  // sem Web Audio liberado: tenta o WAV (funciona se a página já recebeu um toque)
  try {
    const audio = new Audio(buildFallbackUrl())
    audio.volume = 1
    const playing = audio.play()
    if (playing && playing.catch) playing.catch(() => {})
  } catch (e) {
    // sem som disponível neste navegador
  }
}

/** true enquanto o navegador ainda não liberou o som (ninguém tocou na página). */
export const useAlertSoundLocked = () => {
  const [locked, setLocked] = useState(() => {
    const ctx = getContext()
    return ctx ? ctx.state !== 'running' : false
  })
  useEffect(() => {
    const listener = (value) => setLocked(value)
    listeners.add(listener)
    return () => listeners.delete(listener)
  }, [])
  return locked
}

// o primeiro toque, clique ou tecla em qualquer lugar da página libera o som
if (typeof window !== 'undefined') {
  const onGesture = () => {
    unlockAlertSound()
    if (unlocked) {
      window.removeEventListener('pointerdown', onGesture, true)
      window.removeEventListener('keydown', onGesture, true)
      window.removeEventListener('touchstart', onGesture, true)
    }
  }
  window.addEventListener('pointerdown', onGesture, true)
  window.addEventListener('keydown', onGesture, true)
  window.addEventListener('touchstart', onGesture, true)
}
